import { module } from "angular";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import type {
    IFunctionality,
    IFunctionalityInsert,
    IFunctionalityMonitoring,
    IResponse,
    IFunctionalityMotionDetection,
    IAlarmZone,
} from "App/Modules/Models";
import {
    FunctionalityType,
    FunctionalityTypeFunctions,
    VideoStreamFunctions,
    ViewCoverageFunctions,
    AlarmZoneType,
} from "App/Modules/Models";

import type { IHttpService } from "App/Modules/Services";

import type { ISiteFunctionalitiesRepository } from "./ISiteFunctionalitiesRepository";

export default class SiteFunctionalitiesRepository implements ISiteFunctionalitiesRepository {
    public static $inject: string[] = ["mmHttpService"];

    private static transformFunctionality(f: IFunctionality | IFunctionalityMonitoring) {
        f.typeName = f.type as unknown as string;
        f.type = FunctionalityType[f.typeName as keyof typeof FunctionalityType];
        f.icon = FunctionalityTypeFunctions.functionalityTypeIcon(f.type);
    }

    private static transformFunctionalityWithVideoStreams(f: IFunctionality) {
        SiteFunctionalitiesRepository.transformFunctionality(f);

        if (f.videoStream !== null) {
            VideoStreamFunctions.enhanceVideoStream(f.videoStream);
        }
    }

    constructor(private readonly httpService: IHttpService) {}

    public addFunctionality(
        siteId: string,
        functionality: IFunctionalityInsert,
    ): Observable<IResponse<IFunctionality>> {
        return this.httpService.post<IFunctionality>(`sites/${siteId}/functionalities`, functionality).pipe(
            map((response: IResponse<IFunctionality>) => {
                if (!response.data) {
                    return response;
                }

                SiteFunctionalitiesRepository.transformFunctionalityWithVideoStreams(response.data);

                return response;
            }),
        );
    }

    public static enhanceAlarmZone(alarmZone: IAlarmZone): IAlarmZone {
        alarmZone.typeName = alarmZone.type as any as keyof typeof AlarmZoneType;
        alarmZone.type = AlarmZoneType[alarmZone.typeName];
        return alarmZone;
    }

    public getFunctionality<TFunctionality extends IFunctionality>(
        siteId: string,
        functionalityId: string,
    ): Observable<IResponse<TFunctionality>> {
        return this.httpService.get<TFunctionality>(`sites/${siteId}/functionalities/${functionalityId}`).pipe(
            map((response: IResponse<TFunctionality>) => {
                if (!response.data) {
                    return response;
                }

                ViewCoverageFunctions.enhanceViewCoverage(response.data.viewCoverage);
                if (
                    (FunctionalityType[response.data.type] as unknown as FunctionalityType) ===
                    FunctionalityType.MotionDetection
                ) {
                    (response.data as unknown as IFunctionalityMotionDetection).alarmZones.forEach((x) =>
                        SiteFunctionalitiesRepository.enhanceAlarmZone(x),
                    );
                }

                return response;
            }),
        );
    }

    public deleteFunctionality(siteId: string, functionalityId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`sites/${siteId}/functionalities/${functionalityId}`);
    }

    public patchFunctionality<TFunctionality extends IFunctionality>(
        siteId: string,
        functionalityId: string,
        type: FunctionalityType,
        patch: TFunctionality,
    ): Observable<IResponse<TFunctionality>> {
        return this.httpService
            .patch<TFunctionality>(`sites/${siteId}/functionalities/${type}/${functionalityId}`, patch)
            .pipe(
                map((response: IResponse<TFunctionality>) => {
                    if (!response.data) {
                        return response;
                    }

                    return response;
                }),
            );
    }

    public getFunctionalitiesMonitoring(siteId: string): Observable<IResponse<IFunctionalityMonitoring[]>> {
        return this.httpService
            .get<IFunctionalityMonitoring[]>(`sites/${siteId}/functionalities/withMissingConfiguration`)
            .pipe(
                map((response: IResponse<IFunctionalityMonitoring[]>) => {
                    if (!response.data) {
                        return response;
                    }

                    response.data.forEach((functionalityMonitoring: IFunctionalityMonitoring) => {
                        SiteFunctionalitiesRepository.transformFunctionality(functionalityMonitoring);
                    });

                    return response;
                }),
            );
    }

    public getFunctionalitiesForSite(siteId: string): Observable<IResponse<IFunctionality[]>> {
        return this.httpService.get<IFunctionality[]>(`sites/${siteId}/processing`).pipe(
            map((response: IResponse<IFunctionality[]>) => {
                if (!response.data) {
                    return response;
                }

                response.data.forEach((fn) => {
                    SiteFunctionalitiesRepository.transformFunctionality(fn);
                });
                return response;
            }),
        );
    }

    public sendTestAlarm(siteId: string, functionalityId: string): Observable<IResponse<void>> {
        return this.httpService.post(`sites/${siteId}/functionalities/care/${functionalityId}/testalarm`, {});
    }
}

module("mmRepositories").service("mmSiteFunctionalitiesRepository", SiteFunctionalitiesRepository);
